import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, MessageSquare } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { api } from "@/lib/api";
import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/Skeleton";
import { cn } from "@/lib/utils";
import { useDocumentTitle } from "@/lib/useDocumentTitle";
import {
    STATUS_LABELS, STATUS_TONES, formatTicketNumber,
} from "@/lib/supportHelpers";

const FILTER_TABS = [
    { value: "all", label: "All" },
    { value: "open", label: "Open" },
    { value: "awaiting_user", label: "Awaiting reply" },
    { value: "resolved", label: "Resolved" },
    { value: "closed", label: "Closed" },
];

const fmtRel = (iso) => {
    if (!iso) return "—";
    try { return formatDistanceToNow(new Date(iso), { addSuffix: true }); } catch { return iso; }
};
const truncate = (s, n) => (!s ? "" : s.length > n ? s.slice(0, n).trim() + "…" : s);

export default function SupportListPage() {
    const [filter, setFilter] = useState("all");
    const [data, setData] = useState(null);
    useDocumentTitle("Support");

    useEffect(() => {
        let cancelled = false;
        setData(null);
        const params = { page_size: 50 };
        if (filter !== "all") params.status = filter;
        api.get("/support/tickets/mine", { params })
            .then((r) => { if (!cancelled) setData(r.data); });
        return () => { cancelled = true; };
    }, [filter]);

    return (
        <Layout>
            <div className="space-y-6 animate-fade-in-up" data-testid="support-list-page">
                <header className="flex flex-wrap items-end justify-between gap-3">
                    <div className="space-y-1.5">
                        <p className="text-xs font-semibold uppercase tracking-wider text-primary">Support</p>
                        <h1 className="text-3xl font-semibold tracking-tight text-foreground sm:text-4xl">
                            Your support tickets
                        </h1>
                        <p className="text-sm text-muted-foreground">
                            Raise a ticket and we&apos;ll get back to you in the same thread.
                        </p>
                    </div>
                    <Button asChild className="bg-primary hover:bg-primary-hover font-semibold" data-testid="support-new-button">
                        <Link to="/support/new">
                            <Plus className="h-4 w-4" />
                            Raise a ticket
                        </Link>
                    </Button>
                </header>

                <div className="flex flex-wrap gap-2" data-testid="support-filter-chips">
                    {FILTER_TABS.map((t) => (
                        <button
                            key={t.value}
                            onClick={() => setFilter(t.value)}
                            data-testid={`support-filter-${t.value}`}
                            className={cn(
                                "rounded-full border px-3 py-1.5 text-xs font-medium transition-colors",
                                filter === t.value
                                    ? "border-primary bg-primary text-primary-foreground"
                                    : "border-border bg-secondary text-foreground hover:bg-background"
                            )}
                        >
                            {t.label}
                        </button>
                    ))}
                </div>

                {!data ? (
                    <div className="space-y-3">
                        {Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-24 rounded-2xl" />)}
                    </div>
                ) : data.items.length === 0 ? (
                    <Card className="border-border/80 shadow-sm" data-testid="support-empty">
                        <CardContent className="p-10 text-center space-y-3">
                            <MessageSquare className="mx-auto h-8 w-8 text-muted-foreground/50" />
                            <p className="text-base text-foreground">No tickets yet.</p>
                            <p className="text-sm text-muted-foreground">Raise one if you need help.</p>
                            <Button asChild variant="outline">
                                <Link to="/support/new">Raise a ticket</Link>
                            </Button>
                        </CardContent>
                    </Card>
                ) : (
                    <ul className="space-y-3" data-testid="support-list">
                        {data.items.map((t) => (
                            <li key={t.id}>
                                <Link to={`/support/${t.id}`} data-testid={`support-row-${t.id}`} className="block">
                                    <Card className="border-border/80 shadow-sm transition-all hover:-translate-y-0.5 hover:border-primary/40 hover:shadow-md">
                                        <CardContent className="p-5 flex flex-col gap-3 sm:flex-row sm:items-start sm:gap-5">
                                            <div className="min-w-0 flex-1 space-y-1.5">
                                                <div className="flex items-center gap-2">
                                                    <span className="font-mono text-xs text-muted-foreground tabular-nums" data-testid={`support-row-num-${t.id}`}>
                                                        {formatTicketNumber(t.ticket_number)}
                                                    </span>
                                                    <span className={cn("rounded-full border px-2 py-0.5 text-xs font-medium", STATUS_TONES[t.status])} data-testid={`support-row-status-${t.id}`}>
                                                        {STATUS_LABELS[t.status]}
                                                    </span>
                                                    {t.has_unread_for_user && (
                                                        <span className="inline-block h-1.5 w-1.5 rounded-full bg-rose-500" data-testid={`support-row-unread-${t.id}`} aria-label="Unread reply" />
                                                    )}
                                                </div>
                                                <h3 className="text-base font-semibold text-foreground truncate" data-testid={`support-row-subject-${t.id}`}>
                                                    {t.subject}
                                                </h3>
                                                <p className="text-xs text-muted-foreground">
                                                    {t.last_message_by === "admin" ? "Reply from Happy Lab" : "You"}{" "}
                                                    · {fmtRel(t.last_message_at)} · {t.message_count} {t.message_count === 1 ? "message" : "messages"}
                                                </p>
                                            </div>
                                        </CardContent>
                                    </Card>
                                </Link>
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        </Layout>
    );
}
